/**
 * Client API per gli inviti team su core.flusia.it (router `/api/invites*`).
 * Stesso pattern di bcApi.ts: JWT da supabase.auth.getSession(), base URL da
 * VITE_CORE_URL (default: https://core.flusia.it), header X-Flusia-Dev-Key se
 * VITE_GATEWAY_DEV_KEY è impostata.
 *
 * Nota: `lookupInvite` e `acceptInvite` (senza oauthAccessToken) NON mandano
 * Authorization — sono endpoint pubblici del flusso di accettazione invito.
 * `acceptInvite` con `oauthAccessToken` usa quel token esplicito (sessione
 * OAuth in corso), non la sessione Supabase corrente.
 */

import { supabase } from '../supabase';

// ─── DTO ──────────────────────────────────────────────────────────────────────

export interface OrgInvite {
  id: string; email: string; role_id: string; role_name: string;
  status: 'pending' | 'expired' | 'revoked';
  expires_at: string; created_at: string; inviter_name: string;
}

export interface InviteLookup { org_name: string; email: string; role_name: string }

export type InviteErrorCode = 'already_registered' | 'already_invited' | 'seats_full'
  | 'invalid_role' | 'invalid_email' | 'invalid_invite' | 'terms_required'
  | 'invalid_name' | 'weak_password' | 'email_mismatch' | 'unknown';

export interface AcceptInvitePayload {
  token: string;
  firstName: string;
  lastName: string;
  password?: string;
  terms: { version: string; vexatiousAccepted: boolean };
}

// Chiave sessionStorage usata per trasportare i dati di accettazione invito
// attraverso il redirect OAuth (InviteAcceptPage → provider → OAuthCallback →
// acceptInvite via /accept-oauth). Analoga a TERMS_ACCEPTANCE_STORAGE_KEY.
export const INVITE_ACCEPTANCE_STORAGE_KEY = 'flusia_invite_acceptance';

export interface InviteOAuthAcceptancePayload {
  token: string;
  firstName: string;
  lastName: string;
  terms: { version: string; vexatiousAccepted: boolean };
}

// ─── Errore tipizzato ───────────────────────────────────────────────────────────

const KNOWN_ERROR_CODES = new Set<string>([
  'already_registered',
  'already_invited',
  'seats_full',
  'invalid_role',
  'invalid_email',
  'invalid_invite',
  'terms_required',
  'invalid_name',
  'weak_password',
  'email_mismatch',
]);

function toInviteErrorCode(raw: string | undefined): InviteErrorCode {
  if (raw && KNOWN_ERROR_CODES.has(raw)) return raw as InviteErrorCode;
  return 'unknown';
}

export class InviteApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: InviteErrorCode,
  ) {
    super(message);
    this.name = 'InviteApiError';
  }
}

// ─── Interno ────────────────────────────────────────────────────────────────────

const getCoreBase = (): string =>
  (import.meta.env.VITE_CORE_URL as string | undefined) ?? 'https://core.flusia.it';

const getDevKey = (): string | undefined =>
  import.meta.env.VITE_GATEWAY_DEV_KEY as string | undefined;

interface InvitesFetchOptions {
  /** Bearer da mandare esplicitamente (per accept-oauth). Se assente e `auth` è true, usa la sessione Supabase. */
  bearerToken?: string;
  /** Se false, non allega alcun Authorization (endpoint pubblici lookup/accept). Default true. */
  auth?: boolean;
}

async function invitesFetch<T>(
  path: string,
  method: 'GET' | 'POST' | 'DELETE',
  body?: unknown,
  options?: InvitesFetchOptions,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const devKey = getDevKey();
  if (devKey) headers['X-Flusia-Dev-Key'] = devKey;

  const auth = options?.auth ?? true;
  if (auth) {
    if (options?.bearerToken) {
      headers['Authorization'] = `Bearer ${options.bearerToken}`;
    } else {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    }
  }

  const res = await fetch(`${getCoreBase()}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let message = res.statusText;
    let code: InviteErrorCode = 'unknown';
    try {
      const errBody = (await res.json()) as { error?: string };
      if (errBody?.error) {
        message = errBody.error;
        code = toInviteErrorCode(errBody.error);
      }
    } catch {
      // lascia statusText
    }
    throw new InviteApiError(message, res.status, code);
  }

  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

// ─── Endpoint ────────────────────────────────────────────────────────────────────

/** POST /api/invites — crea un invito per email+ruolo. */
export async function createInvite(
  email: string,
  roleId: string,
): Promise<{ invite: OrgInvite; invite_url: string }> {
  return invitesFetch<{ invite: OrgInvite; invite_url: string }>('/api/invites', 'POST', {
    email,
    role_id: roleId,
  });
}

/** Superadmin: crea un invito in un'azienda qualsiasi. */
export async function adminCreateInvite(organizationId: string, email: string, roleId: string): Promise<void> {
  await invitesFetch(`/api/admin/organizations/${organizationId}/invites`, 'POST', { email, role_id: roleId });
}

/** Superadmin: rigenera il link e rimanda l'email. */
export async function adminResendInvite(organizationId: string, id: string): Promise<void> {
  await invitesFetch(`/api/admin/organizations/${organizationId}/invites/${id}/resend`, 'POST');
}

/** Superadmin: revoca un invito in attesa. */
export async function adminRevokeInvite(organizationId: string, id: string): Promise<void> {
  await invitesFetch(`/api/admin/organizations/${organizationId}/invites/${id}`, 'DELETE');
}

/** GET /api/invites — lista inviti dell'organizzazione corrente. */
export async function listInvites(): Promise<OrgInvite[]> {
  const { invites } = await invitesFetch<{ invites: OrgInvite[] }>('/api/invites', 'GET');
  return invites;
}

/** POST /api/invites/:id/resend — rigenera token e reinvia l'email di invito. */
export async function resendInvite(id: string): Promise<{ invite: OrgInvite; invite_url: string }> {
  return invitesFetch<{ invite: OrgInvite; invite_url: string }>(
    `/api/invites/${id}/resend`,
    'POST',
  );
}

/** DELETE /api/invites/:id — revoca un invito pendente. */
export async function revokeInvite(id: string): Promise<void> {
  await invitesFetch<{ success: true }>(`/api/invites/${id}`, 'DELETE');
}

/** GET /api/invites/lookup/:token — dati pubblici invito (NO auth). */
export async function lookupInvite(token: string): Promise<InviteLookup> {
  return invitesFetch<InviteLookup>(`/api/invites/lookup/${token}`, 'GET', undefined, {
    auth: false,
  });
}

/**
 * POST /api/invites/accept oppure /api/invites/accept-oauth.
 * Senza `oauthAccessToken`: accettazione via password (NO auth), `payload.password` richiesto.
 * Con `oauthAccessToken`: accettazione via sessione OAuth già autenticata, `payload.password` ignorato.
 */
export async function acceptInvite(payload: AcceptInvitePayload, oauthAccessToken?: string): Promise<void> {
  if (oauthAccessToken) {
    const { password, ...oauthPayload } = payload;
    void password;
    await invitesFetch<{ success: true }>('/api/invites/accept-oauth', 'POST', oauthPayload, {
      auth: true,
      bearerToken: oauthAccessToken,
    });
    return;
  }

  await invitesFetch<{ success: true }>('/api/invites/accept', 'POST', payload, { auth: false });
}
